/**
 * Shared step building blocks for the Drawings tours.
 *
 * Drawings is structured as a single page (``PDFEditorPage``) that toggles
 * between two modes inside ``PDFWorkspace``:
 *
 *   - **Browser mode** — when no PDF is open. ``DrawingsBrowser`` renders a
 *     list/grid of every PDF on the project, with upload + bulk actions.
 *   - **Editor mode** — when the user has clicked into a drawing. The
 *     ``DrawingsBrowser`` is unmounted and ``PDFToolbar`` + the canvas
 *     viewport take over the full screen via a portal.
 *
 * The ``combined`` tour walks both, the ``list`` tour covers just the
 * browser, and the ``editor`` tour covers just the editor.
 */
import type { TourStep } from '../types';
import { clickPreferReact, has, waitForCondition } from '../utils/dom';

/**
 * Predicate: is the editor mode currently mounted? We key on the toolbar
 * marker rather than ``document !== null`` because (a) the tour runs
 * outside React state and (b) the toolbar is always there in editor mode.
 */
export const hasEditorMounted = (): boolean =>
  has('[data-tour-id="drawings-editor-toolbar"]');

// ─── Browser-side ────────────────────────────────────────────────────────────

export const uploadStep: TourStep = {
  id: 'upload',
  target: '[data-tour-id="drawings-upload"]',
  placement: 'left',
  spotlightPadding: 4,
  title: 'Upload drawings',
  body:
    'Click here to upload PDFs — single sheet or full sheet sets. We\u2019ll ' +
    'auto-detect drawing numbers, titles, and disciplines from the title block.',
};

export const searchFiltersStep: TourStep = {
  id: 'search-filters',
  target: '[data-tour-id="drawings-search-filters"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Search & filter',
  body:
    'Search by drawing number, title, filename, or discipline. Use the ' +
    'discipline / status filters and view toggle (grid vs. list) to find ' +
    'what you need fast.',
};

export const bulkActionsStep: TourStep = {
  id: 'bulk-actions',
  target: '[data-tour-id="drawings-bulk-actions"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Multi-select & download',
  body:
    'Tick drawings to select them, then download originals (just the PDFs) ' +
    'or download with markup baked in. You can also delete in bulk.',
};

export const listStep: TourStep = {
  id: 'list',
  target: '[data-tour-id="drawings-list"]',
  placement: 'top',
  spotlightPadding: 4,
  title: 'Browse the project drawings',
  body:
    'Drawings are grouped by discipline. Click any drawing to open it ' +
    'full-screen in the editor.',
};

export const combinedListStep: TourStep = {
  id: 'open-a-drawing',
  target: '[data-tour-id="drawings-list"]',
  placement: 'top',
  spotlightPadding: 4,
  title: 'Open a drawing',
  body:
    'Drawings are grouped by discipline. Click any drawing to open it. ' +
    'Press Next and we\u2019ll open the first one for you.',
  onBeforeNext: async () => {
    clickPreferReact('[data-tour-id="drawings-row"]');
    // Editor load is async (PDF download + worker boot). Generous timeout.
    await waitForCondition(hasEditorMounted, 8000, 'row click → editor');
  },
  autoAdvanceWhen: () => hasEditorMounted(),
};

// ─── Editor-side ─────────────────────────────────────────────────────────────

export const filePickerStep: TourStep = {
  id: 'file-picker',
  target: '[data-tour-id="drawings-file-picker"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Switch between drawings',
  body:
    'Jump to any other drawing on the project without leaving the editor.',
};

export const revisionPickerStep: TourStep = {
  id: 'revision-picker',
  target: '[data-tour-id="drawings-revision-picker"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Compare revisions',
  body:
    'Switch between revisions of the same sheet. Publish a draft revision ' +
    'as the current one when it\u2019s ready.',
};

export const pageNavStep: TourStep = {
  id: 'page-nav',
  target: '[data-tour-id="drawings-page-nav"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Step through pages',
  body:
    'Page through multi-sheet PDFs. Each page is treated as its own sheet — ' +
    'with its own revisions, markups, and history.',
};

export const shapesStep: TourStep = {
  id: 'shapes',
  target: '[data-tour-id="drawings-shapes"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Markup tools',
  body:
    'Draw revision clouds, arrows, text boxes, and pins right on the sheet. ' +
    'Pins can link to a punch item, observation, RFI, or submittal.',
};

export const markupFilterStep: TourStep = {
  id: 'markup-filter',
  target: '[data-tour-id="drawings-markup-filter"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Show whose markup',
  body:
    'Cycle between All / Personal / Shared markups so you can focus on your ' +
    'own work, the team\u2019s, or both.',
};

export const activityStep: TourStep = {
  id: 'activity',
  target: '[data-tour-id="drawings-activity"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Recent activity',
  body:
    'Toggle the activity panel to see who\u2019s edited this sheet recently — ' +
    'great for catching up after time away.',
};

export const searchStep: TourStep = {
  id: 'search-text',
  target: '[data-tour-id="drawings-search-text"]',
  placement: 'top',
  spotlightPadding: 4,
  title: 'Search text in the sheet',
  body:
    'Find any word inside the PDF. Press Enter to step through matches; ' +
    'Shift+Enter goes back. Works across every page of the drawing.',
};

export const closeStep: TourStep = {
  id: 'close',
  target: '[data-tour-id="drawings-close"]',
  placement: 'left',
  spotlightPadding: 4,
  title: 'Back to the browser',
  body:
    'Hit Close to go back to the project drawings list. Your markups are ' +
    'saved automatically as you go.',
};
